import {
  AsyncButton,
  ButtonSize,
  ButtonStyle,
  capitalize,
  colors,
  IGiftItemResponse,
  StateAction,
} from '@one-tree/library'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import React, { ReactElement } from 'react'
import { FiMove as MoveIcon } from 'react-icons/fi'
import styled from 'styled-components'
import { IGiftItemPostBody } from '../../../../../types/GiftItemTypes'
import { PortalTicketQuestion } from '../../../../../types/Types'
import { priceUsingQuestion } from '../TicketPrices/pricesHelper'
import EnableQuestion from './EnableQuestion'

const Option = styled.div`
  margin-bottom: 4px;
  &:last-child {
    margin-bottom: 0px;
  }
`
const OptionNumber = styled.span`
  color: ${colors.darkerGray};
`
const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  padding: 2px;
  color: ${colors.darkGray};
  &:active {
    cursor: grabbing;
  }
`

interface ISortableRowProps {
  question: PortalTicketQuestion
  ticket: IGiftItemResponse
  changeTicket: (changes: IGiftItemPostBody) => void
  onToggleQuestion: (enabledQuestionIds: number[]) => void
  onPriceToggle: (priceId: number, enabledQuestionIds: number[]) => void
  disableDelete: number[]
  setDisableDelete: StateAction<number[]>
  onDelete: (questionId: number) => Promise<void>
}

export default function SortableRow(props: ISortableRowProps): ReactElement {
  const {
    question,
    ticket,
    changeTicket,
    onToggleQuestion,
    onPriceToggle,
    disableDelete,
    setDisableDelete,
    onDelete,
  } = props

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.id })

  const style = {
    transform: CSS.Translate.toString(transform),
    transition: [transition, isDragging ? null : 'opacity 200ms']
      .filter(Boolean)
      .join(', '),
    opacity: isDragging ? 0.5 : 1,
  }

  const questions = ticket.enabledQuestionIds ?? []
  const { options } = question

  const renderOptions = options
    && options.length > 0
    && options.map((option, index) => (
      <Option key={option.value}>
        <OptionNumber>{`${index + 1}. `}</OptionNumber>
        <span>{option.label}</span>
      </Option>
    ))

  const isRequired = question.rules && question.rules.includes('required')
      ? 'Required'
      : 'Optional'

  const parentUsingQuestion = ticket.enabledQuestionIds
    && ticket.enabledQuestionIds.length > 0
    && ticket.enabledQuestionIds.includes(question.id)

  // Only enabled questions can be reordered, so hide the handle on the rest
  const isEnabled = Boolean(parentUsingQuestion)
    || priceUsingQuestion(question.id, ticket.prices)

  const questionInUse = isEnabled || disableDelete.includes(question.id) || question.used

  return (
    <tr ref={setNodeRef} style={style}>
      <td>
        {isEnabled && (
          <DragHandle {...attributes} {...listeners}>
            <MoveIcon size={20} />
          </DragHandle>
        )}
      </td>
      <td aria-label="Enable question">
        <EnableQuestion
          question={question}
          questions={questions}
          ticket={ticket}
          changeTicket={changeTicket}
          onToggleQuestion={onToggleQuestion}
          onPriceToggle={onPriceToggle}
          setDisableDelete={setDisableDelete}
        />
      </td>
      <td style={{ maxWidth: '300px' }}>{question.title}</td>
      <td style={{ textWrap: 'nowrap' }}>
        {`${isRequired} ${capitalize(question.type)}`}
      </td>
      <td>{renderOptions}</td>
      <td>
        <AsyncButton
          onClick={(): Promise<void> => onDelete(question.id)}
          buttonStyle={ButtonStyle.Secondary}
          buttonSize={ButtonSize.Table}
          showConfirmation={true}
          disabled={questionInUse}
          style={{ width: '46px' }}
        >
          {questionInUse ? 'In use' : 'Delete'}
        </AsyncButton>
      </td>
    </tr>
  )
}
